import { useState } from 'react';
import { formatDateTime } from '../lib/format.js';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Chip,
  Switch,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import BoltIcon from '@mui/icons-material/Bolt';
import { useMe } from '../api/hooks.js';
import { api } from '../api/client.js';
import { useT } from '../i18n/useT.js';
import { useSort } from '../lib/useSort.js';

interface Rule {
  id: string;
  name: string;
  metric: string;
  metricLabel: string;
  threshold: number;
  windowMinutes: number;
  cooldownMinutes: number;
  notifyProviders: string[];
  ticketIntegrationId?: string | null;
  notifyInApp: boolean;
  enabled: boolean;
  lastFiredAt?: string;
}
interface Event {
  id: string;
  /** null for a package-limit notice, which no rule fired. */
  ruleName: string | null;
  planLimit: { metric: string; level: number; limit: number } | null;
  metricLabel: string;
  value: number;
  threshold: number;
  message: string;
  read: boolean;
  createdAt: string;
}

const METRICS = [
  'HIGH_FRUSTRATION_SESSIONS',
  'ERROR_OCCURRENCES',
  'RAGE_SESSIONS',
  'NEW_ERROR_GROUPS',
];
const WINDOWS = [15, 60, 360, 1440];
const COOLDOWNS = [0, 30, 60, 360];
const NOTIFIERS = [{ id: 'slack', label: 'Slack' }, { id: 'teams', label: 'Microsoft Teams' }];
const fmtWindow = (m: number) => (m >= 60 ? `${m / 60}h` : `${m}m`);

export function AlertsPage() {
  const all = useT();
  const sort = useSort<'when' | 'rule' | 'detail'>('when');
  const t = all.alerts;
  const common = all.common;
  const { data: me } = useMe();
  const orgId = me?.organizations[0]?.id;
  const isAdmin = ['OWNER', 'ADMIN'].includes(me?.organizations[0]?.role ?? '');
  const base = `/v1/orgs/${orgId}/alerts`;
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<{ open: boolean; rule?: Rule }>({ open: false });

  const rules = useQuery({
    enabled: !!orgId,
    queryKey: ['alert-rules', orgId],
    queryFn: async () => (await api.get(`${base}/rules`)).data.data as Rule[],
  });
  const events = useQuery({
    enabled: !!orgId,
    queryKey: ['alert-events', orgId],
    queryFn: async () => (await api.get(`${base}/events`)).data.data as Event[],
  });
  const eventRows = sort.sorted(events.data ?? [], {
    when: (e) => e.createdAt,
    rule: (e) => e.ruleName ?? '',
    detail: (e) => e.message,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['alert-rules', orgId] });
    qc.invalidateQueries({ queryKey: ['alert-events', orgId] });
    qc.invalidateQueries({ queryKey: ['notifications', orgId] });
  };

  const toggle = useMutation({
    mutationFn: async (r: Rule) => api.patch(`${base}/rules/${r.id}`, { enabled: !r.enabled }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: async (id: string) => api.delete(`${base}/rules/${id}`),
    onSuccess: invalidate,
  });
  const evaluate = useMutation({
    mutationFn: async () => (await api.post(`${base}/evaluate`)).data as { fired: number },
    onSuccess: invalidate,
  });

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <NotificationsActiveIcon color="primary" />
            <Typography variant="h5">{t.title}</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {t.subtitle}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<BoltIcon />} disabled={!isAdmin || evaluate.isPending} onClick={() => evaluate.mutate()}>
            {evaluate.isPending ? t.evaluating : t.evaluateNow}
          </Button>
          {isAdmin && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ open: true })}>
              {t.newRule}
            </Button>
          )}
        </Stack>
      </Stack>

      {evaluate.data && (
        <Paper sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover' }}>
          <Typography variant="body2">{t.evaluated(evaluate.data.fired)}</Typography>
        </Paper>
      )}

      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>{t.rules}</Typography>
      <Paper sx={{ overflow: 'hidden', mb: 3 }}>
        <Stack divider={<Divider flexItem />}>
          {(rules.data ?? []).map((r) => (
            <Stack key={r.id} direction="row" alignItems="center" spacing={2} sx={{ px: 2, py: 1.5 }}>
              <Switch checked={r.enabled} onChange={() => toggle.mutate(r)} disabled={!isAdmin} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600}>{r.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {t.ruleSummary(
                    t.metrics[r.metric] ?? r.metricLabel,
                    r.threshold,
                    fmtWindow(r.windowMinutes),
                    fmtWindow(r.cooldownMinutes),
                  )}
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.5}>
                {r.notifyInApp && <Chip size="small" label={t.inApp} variant="outlined" />}
                {r.notifyProviders.map((p) => (<Chip key={p} size="small" label={p} variant="outlined" />))}
              </Stack>
              {isAdmin && (
                <>
                  <Button size="small" onClick={() => setDialog({ open: true, rule: r })}>{common.edit}</Button>
                  <IconButton size="small" color="error" onClick={() => remove.mutate(r.id)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                </>
              )}
            </Stack>
          ))}
          {rules.data && rules.data.length === 0 && (
            <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
              {t.noRules}
            </Typography>
          )}
        </Stack>
      </Paper>

      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>{t.recent}</Typography>
      <Paper sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 600 }}>
          <TableHead>
            <TableRow>
              {sort.head('when', t.columns.when)}
              {sort.head('rule', t.columns.rule)}
              {sort.head('detail', t.columns.detail)}
            </TableRow>
          </TableHead>
          <TableBody>
            {eventRows.map((e) => (
              <TableRow key={e.id} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}><Typography variant="caption" color="text.secondary">{formatDateTime(e.createdAt)}</Typography></TableCell>
                <TableCell><Chip size="small" label={e.planLimit ? all.shell.limits.title : e.ruleName} color="warning" variant="outlined" /></TableCell>
                <TableCell><Typography variant="body2">{e.planLimit ? all.shell.limits.notice(e.planLimit.metric, e.planLimit.level) : e.message}</Typography></TableCell>
              </TableRow>
            ))}
            {events.data && events.data.length === 0 && (
              <TableRow><TableCell colSpan={3}><Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>{t.noEvents}</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <RuleDialog
        key={dialog.rule?.id ?? 'new'}
        open={dialog.open}
        base={base}
        rule={dialog.rule}
        orgId={orgId ?? ''}
        onClose={() => setDialog({ open: false })}
        onSaved={() => { setDialog({ open: false }); invalidate(); }}
      />
    </Box>
  );
}

function RuleDialog({ open, base, orgId, rule, onClose, onSaved }: { open: boolean; base: string; orgId: string; rule?: Rule; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(rule?.name ?? '');
  const [metric, setMetric] = useState(rule?.metric ?? 'HIGH_FRUSTRATION_SESSIONS');
  const [threshold, setThreshold] = useState(rule?.threshold ?? 5);
  const [windowMinutes, setWindowMinutes] = useState(rule?.windowMinutes ?? 60);
  const [cooldownMinutes, setCooldownMinutes] = useState(rule?.cooldownMinutes ?? 60);
  const [providers, setProviders] = useState<string[]>(rule?.notifyProviders ?? []);
  const [notifyInApp, setNotifyInApp] = useState(rule?.notifyInApp ?? true);
  const [ticketIntegrationId, setTicketIntegrationId] = useState<string>(rule?.ticketIntegrationId ?? '');

  // Issue-capable profiles across the org, for the auto-ticket select.
  const projects = useQuery({
    enabled: open && !!orgId,
    queryKey: ['projects', orgId],
    queryFn: async () => (await api.get(`/v1/orgs/${orgId}/projects`)).data.data as { id: string }[],
  });
  const trackers = useQuery({
    enabled: open && !!orgId && !!projects.data?.length,
    queryKey: ['org-trackers', orgId],
    queryFn: async () =>
      (await api.get(`/v1/orgs/${orgId}/projects/${projects.data![0]!.id}/integrations/trackers`)).data
        .data as { id: string; provider: string; label: string; target: string }[],
  });

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name, metric, threshold, windowMinutes, cooldownMinutes, notifyProviders: providers, notifyInApp,
        ticketIntegrationId: ticketIntegrationId || null,
      };
      return rule ? api.patch(`${base}/rules/${rule.id}`, body) : api.post(`${base}/rules`, body);
    },
    onSuccess: onSaved,
  });

  const toggleProvider = (id: string) =>
    setProviders((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const all = useT();
  const t = all.alerts.dialog;
  const metricNames = all.alerts.metrics;
  const common = all.common;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{rule ? t.edit : t.create}</DialogTitle>
      <DialogContent>
        <TextField fullWidth margin="normal" label={t.name} value={name} onChange={(e) => setName(e.target.value)} placeholder={t.namePlaceholder} />
        <TextField select fullWidth margin="normal" label={t.metric} value={metric} onChange={(e) => setMetric(e.target.value)}>
          {METRICS.map((m) => (<MenuItem key={m} value={m}>{metricNames[m] ?? m}</MenuItem>))}
        </TextField>
        <Stack direction="row" spacing={2}>
          <TextField type="number" margin="normal" label={t.threshold} value={threshold} onChange={(e) => setThreshold(Number(e.target.value))} sx={{ width: 160 }} />
          <TextField select margin="normal" label={t.window} value={windowMinutes} onChange={(e) => setWindowMinutes(Number(e.target.value))} fullWidth>
            {WINDOWS.map((w) => (<MenuItem key={w} value={w}>{fmtWindow(w)}</MenuItem>))}
          </TextField>
          <TextField select margin="normal" label={t.cooldown} value={cooldownMinutes} onChange={(e) => setCooldownMinutes(Number(e.target.value))} fullWidth>
            {COOLDOWNS.map((c) => (<MenuItem key={c} value={c}>{c === 0 ? t.none : fmtWindow(c)}</MenuItem>))}
          </TextField>
        </Stack>
        <Divider sx={{ my: 1.5 }}><Typography variant="caption" color="text.secondary">{t.notify}</Typography></Divider>
        <FormControlLabel control={<Checkbox checked={notifyInApp} onChange={(e) => setNotifyInApp(e.target.checked)} />} label={t.inApp} />
        {NOTIFIERS.map((n) => (
          <FormControlLabel key={n.id} control={<Checkbox checked={providers.includes(n.id)} onChange={() => toggleProvider(n.id)} />} label={n.label} />
        ))}
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
          {t.providerHint}
        </Typography>
        <Divider sx={{ my: 1.5 }}><Typography variant="caption" color="text.secondary">{t.followUp}</Typography></Divider>
        <TextField
          select
          fullWidth
          margin="normal"
          label={t.openTicketIn}
          value={ticketIntegrationId}
          onChange={(e) => setTicketIntegrationId(e.target.value)}
          helperText={t.openTicketHint}
          disabled={(trackers.data ?? []).length === 0}
        >
          <MenuItem value="">
            <em>{t.noTicket}</em>
          </MenuItem>
          {(trackers.data ?? []).map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.provider}
              {t.label !== 'default' ? ` (${t.label})` : ''}
              {t.target ? `, ${t.target}` : ''}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{common.cancel}</Button>
        <Button variant="contained" disabled={!name.trim() || threshold < 1 || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? t.saving : t.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
